<template>
  <div class="container-fluid">
    <div class="row mt-3">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h5 class="mb-0"><i class="fa fa-plus-circle me-2"></i>Registrar nuevo instrumento</h5>
            <button class="btn btn-outline-secondary btn-sm" @click="$router.back()">
              <i class="fa fa-arrow-left me-1"></i> Regresar
            </button>
          </div>
          <div class="card-body">

            <!-- Alerta de éxito -->
            <div v-if="successMsg" class="alert alert-success d-flex align-items-center">
              <i class="fa fa-check-circle me-2"></i> {{ successMsg }}
            </div>

            <!-- Alerta de error -->
            <div v-if="errorMsg" class="alert alert-danger d-flex align-items-center">
              <i class="fa fa-exclamation-circle me-2"></i> {{ errorMsg }}
            </div>

            <form @submit.prevent="submitForm" novalidate>

              <!-- ── Información general ── -->
              <h6 class="fw-bold mb-3 mt-2" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Información general
              </h6>
              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <label class="form-label">Nombre <span class="text-danger">*</span></label>
                  <input v-model="form.nombre" type="text" class="form-control" :class="{'is-invalid': errors.nombre}" placeholder="Nombre del instrumento" />
                  <div class="invalid-feedback">Este campo es requerido.</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Tipo <span class="text-danger">*</span></label>
                  <select v-model="form.tipo" class="form-select" :class="{'is-invalid': errors.tipo}" :disabled="loadingCatalogo">
                    <option :value="null" disabled>{{ loadingCatalogo ? 'Cargando…' : 'Selecciona un tipo' }}</option>
                    <option v-for="t in catalogoTipos" :key="t.id" :value="t.id">{{ t.nombre }}</option>
                  </select>
                  <div class="invalid-feedback">Selecciona un tipo.</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Sector</label>
                  <div class="ms-wrap" :class="{ 'ms-wrap--open': openDropdown === 'sector' }">
                    <button type="button" class="ms-trigger" :disabled="loadingCatalogo" @click.stop="toggleDropdown('sector')">
                      <span v-if="!form.sector.length" class="ms-placeholder">
                        {{ loadingCatalogo ? 'Cargando…' : 'Sin especificar' }}
                      </span>
                      <span v-else class="ms-tags">
                        <span v-for="id in form.sector" :key="id" class="ms-tag">
                          {{ labelById(catalogoSectores, id) }}<button type="button" @click.stop="removeItem('sector', id)">×</button>
                        </span>
                      </span>
                      <i class="fa fa-angle-down ms-arrow"></i>
                    </button>
                    <div class="ms-menu" @click.stop>
                      <label v-for="s in catalogoSectores" :key="s.id" class="ms-item">
                        <input type="checkbox" :value="s.id" v-model="form.sector" /> {{ s.nombre }}
                      </label>
                      <p v-if="!catalogoSectores.length" class="ms-empty">Sin opciones disponibles</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Estado</label>
                  <div class="ms-wrap" :class="{ 'ms-wrap--open': openDropdown === 'estado' }">
                    <button type="button" class="ms-trigger" :disabled="loadingCatalogo" @click.stop="toggleDropdown('estado')">
                      <span v-if="!form.estado.length" class="ms-placeholder">
                        {{ loadingCatalogo ? 'Cargando…' : 'Sin especificar' }}
                      </span>
                      <span v-else class="ms-tags">
                        <span v-for="id in form.estado" :key="id" class="ms-tag">
                          {{ labelById(catalogoEstados, id) }}<button type="button" @click.stop="removeItem('estado', id)">×</button>
                        </span>
                      </span>
                      <i class="fa fa-angle-down ms-arrow"></i>
                    </button>
                    <div class="ms-menu" @click.stop>
                      <label v-for="e in catalogoEstados" :key="e.id" class="ms-item">
                        <input type="checkbox" :value="e.id" v-model="form.estado" /> {{ e.nombre }}
                      </label>
                      <p v-if="!catalogoEstados.length" class="ms-empty">Sin opciones disponibles</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Escala</label>
                  <div class="ms-wrap" :class="{ 'ms-wrap--open': openDropdown === 'escala' }">
                    <button type="button" class="ms-trigger" :disabled="loadingCatalogo" @click.stop="toggleDropdown('escala')">
                      <span v-if="!form.escala.length" class="ms-placeholder">
                        {{ loadingCatalogo ? 'Cargando…' : 'Sin especificar' }}
                      </span>
                      <span v-else class="ms-tags">
                        <span v-for="id in form.escala" :key="id" class="ms-tag">
                          {{ labelById(catalogoEscalas, id) }}<button type="button" @click.stop="removeItem('escala', id)">×</button>
                        </span>
                      </span>
                      <i class="fa fa-angle-down ms-arrow"></i>
                    </button>
                    <div class="ms-menu" @click.stop>
                      <label v-for="e in catalogoEscalas" :key="e.id" class="ms-item">
                        <input type="checkbox" :value="e.id" v-model="form.escala" /> {{ e.nombre }}
                      </label>
                      <p v-if="!catalogoEscalas.length" class="ms-empty">Sin opciones disponibles</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Periodicidad</label>
                  <select v-model="form.periodicidad" class="form-select">
                    <option value="">Sin especificar</option>
                    <option value="unica">Única vez</option>
                    <option value="mensual">Mensual</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">Costo</label>
                  <input v-model="form.costo" type="text" class="form-control" placeholder="Ej. $0" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Monto</label>
                  <input v-model="form.monto" type="text" class="form-control" placeholder="Ej. $500,000.00" />
                </div>
              </div>

              <!-- ── Fechas ── -->
              <h6 class="fw-bold mb-3" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Vigencia
              </h6>
              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <label class="form-label">Fecha de inicio</label>
                  <input v-model="form.fecha_inicio" type="date" class="form-control" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Fecha de fin</label>
                  <input v-model="form.fecha_fin" type="date" class="form-control" />
                </div>
              </div>

              <!-- ── Contenido enriquecido ── -->
              <h6 class="fw-bold mb-3" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Descripción y contenido
              </h6>
              <div class="row g-3 mb-4">
                <div class="col-12">
                  <label class="form-label">Descripción</label>
                  <textarea v-model="form.descripcion" class="form-control" rows="4" placeholder="Descripción del instrumento..."></textarea>
                  <small class="text-muted">Acepta HTML enriquecido.</small>
                </div>
                <div class="col-12">
                  <label class="form-label">Objetivo</label>
                  <textarea v-model="form.objetivo" class="form-control" rows="3" placeholder="Objetivo del instrumento..."></textarea>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Público objetivo</label>
                  <textarea v-model="form.publico_objetivo" class="form-control" rows="3" placeholder="PyMEs, Gobierno, etc."></textarea>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Modalidad</label>
                  <textarea v-model="form.modalidad" class="form-control" rows="3" placeholder="Virtual, presencial, híbrida..."></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label">Requisitos</label>
                  <textarea v-model="form.requisitos" class="form-control" rows="4" placeholder="Requisitos para participar..."></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label">Resultados esperados</label>
                  <textarea v-model="form.resultados" class="form-control" rows="3" placeholder="Resultados esperados..."></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label">Criterios ASG</label>
                  <textarea v-model="form.criterios_asg" class="form-control" rows="3" placeholder="Criterios ambientales, sociales y de gobernanza..."></textarea>
                </div>
              </div>

              <!-- ── Indicadores y ODS ── -->
              <h6 class="fw-bold mb-3" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Indicadores y ODS
              </h6>
              <div class="row g-3 mb-4">
                <div class="col-md-4">
                  <label class="form-label">Indicador de impacto</label>
                  <input v-model="form.indicador_impacto" type="text" class="form-control" placeholder="KPI1" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Meta cuantitativa</label>
                  <input v-model="form.meta_cuantitativa" type="text" class="form-control" placeholder="Ej. No aplica" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">ODS relacionados</label>
                  <input v-model="form.ods_relacionados" type="text" class="form-control" placeholder="Ej. ODS 7, ODS 13" />
                </div>
                <div class="col-12">
                  <label class="form-label">Cadenas de valor</label>
                  <input v-model="form.cadenas_valor" type="text" class="form-control" placeholder="Verde, renovables, ..." />
                </div>
              </div>

              <!-- ── Contacto y enlace ── -->
              <h6 class="fw-bold mb-3" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Contacto y enlace
              </h6>
              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <label class="form-label">Enlace web</label>
                  <input v-model="form.enlace_web" type="url" class="form-control" placeholder="https://ejemplo.com" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Correo de contacto</label>
                  <input v-model="form.contacto_email" type="email" class="form-control" placeholder="contacto@ejemplo.com" />
                </div>
              </div>

              <!-- ── Notas ── -->
              <h6 class="fw-bold mb-3" style="color:#182e50; border-bottom:2px solid #e0e6f0; padding-bottom:6px;">
                Notas adicionales
              </h6>
              <div class="mb-4">
                <textarea v-model="form.notas_adicionales" class="form-control" rows="3" placeholder="Información adicional relevante..."></textarea>
              </div>

              <!-- Botones -->
              <div class="d-flex gap-2 justify-content-end">
                <button type="button" class="btn btn-outline-secondary" @click="$router.back()">
                  Cancelar
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  <span v-if="saving"><i class="fa fa-spinner fa-spin me-1"></i> Guardando...</span>
                  <span v-else><i class="fa fa-save me-1"></i> Registrar instrumento</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axios";

export default {
  name: "NuevoInstrumento",
  data() {
    return {
      saving: false,
      successMsg: "",
      errorMsg: "",
      errors: {},
      loadingCatalogo: false,
      catalogoTipos: [],
      catalogoSectores: [],
      catalogoEstados: [],
      catalogoEscalas: [],
      openDropdown: null,
      form: {
        nombre: "",
        tipo: null,
        descripcion: "",
        objetivo: "",
        publico_objetivo: "",
        sector: [],
        estado: [],
        escala: [],
        requisitos: "",
        modalidad: "",
        fecha_inicio: "",
        fecha_fin: "",
        resultados: "",
        criterios_asg: "",
        enlace_web: "",
        contacto_email: "",
        cadenas_valor: "",
        periodicidad: "",
        costo: "",
        monto: "",
        indicador_impacto: "",
        meta_cuantitativa: "",
        ods_relacionados: "",
        notas_adicionales: "",
      },
    };
  },
  created() {
    this.fetchCatalogo();
  },
  mounted() {
    document.addEventListener("click", this.cerrarDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.cerrarDropdown);
  },
  methods: {
    labelById(catalogo, id) {
      return catalogo.find((c) => c.id === id)?.nombre ?? String(id);
    },
    toggleDropdown(nombre) {
      this.openDropdown = this.openDropdown === nombre ? null : nombre;
    },
    cerrarDropdown() {
      this.openDropdown = null;
    },
    removeItem(campo, id) {
      this.form[campo] = this.form[campo].filter((v) => v !== id);
    },
    async fetchCatalogo() {
      this.loadingCatalogo = true;
      try {
        const { data } = await api.get("catalogos");
        this.catalogoTipos    = data.tipos_oportunidad ?? [];
        this.catalogoSectores = data.sectores          ?? [];
        this.catalogoEstados  = data.estados           ?? [];
        this.catalogoEscalas  = data.escalas           ?? [];
      } catch {
        // combos permanecen vacíos; el formulario sigue usable
      } finally {
        this.loadingCatalogo = false;
      }
    },
    validate() {
      this.errors = {};
      if (!this.form.nombre.trim()) this.errors.nombre = true;
      if (!this.form.tipo) this.errors.tipo = true;
      return Object.keys(this.errors).length === 0;
    },
    buildPayload() {
      const p = { ...this.form };
      ["sector", "estado", "escala"].forEach((k) => {
        if (!p[k].length) delete p[k];
      });
      ["fecha_inicio", "fecha_fin"].forEach((k) => {
        if (!p[k]) delete p[k];
      });
      return p;
    },
    async submitForm() {
      this.successMsg = "";
      this.errorMsg = "";
      if (!this.validate()) return;

      this.saving = true;
      try {
        await api.post("instrumentos/crear/", this.buildPayload());
        this.successMsg = "Instrumento registrado exitosamente.";
        window.scrollTo({ top: 0, behavior: "smooth" });
        this.resetForm();
      } catch (err) {
        const detail = err.response?.data
          ? JSON.stringify(err.response.data)
          : err.message;
        this.errorMsg = `Error al registrar: ${detail}`;
        window.scrollTo({ top: 0, behavior: "smooth" });
        console.error("Error al registrar instrumento:", err);
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      Object.keys(this.form).forEach((k) => {
        if (["sector", "estado", "escala"].includes(k)) this.form[k] = [];
        else if (k === "tipo") this.form[k] = null;
        else this.form[k] = "";
      });
    },
  },
};
</script>

<style scoped>
.ms-wrap {
  position: relative;
}
.ms-trigger {
  width: 100%; min-height: 38px; padding: .375rem .75rem;
  display: flex; align-items: flex-start; gap: .4rem; flex-wrap: wrap;
  background: #fff; border: 1px solid #ced4da; border-radius: .375rem;
  font-size: .875rem; color: #212529; cursor: pointer; text-align: left;
  transition: border-color .15s, box-shadow .15s;
}
.ms-trigger:focus, .ms-wrap--open .ms-trigger {
  border-color: #86b7fe; outline: 0;
  box-shadow: 0 0 0 .25rem rgba(13,110,253,.25);
}
.ms-trigger:disabled { background: #e9ecef; cursor: not-allowed; }
.ms-placeholder { color: #6c757d; }
.ms-tags { display: flex; flex-wrap: wrap; gap: .25rem; flex: 1; }
.ms-tag {
  display: inline-flex; align-items: center; gap: .25rem;
  background: #09377c; color: #fff; border-radius: 4px;
  padding: .1rem .45rem; font-size: .78rem;
}
.ms-tag button {
  background: none; border: none; color: inherit;
  font-size: .9rem; padding: 0; line-height: 1; cursor: pointer; opacity: .75;
}
.ms-tag button:hover { opacity: 1; }
.ms-arrow { margin-left: auto; color: #6c757d; flex-shrink: 0; align-self: center; }
.ms-menu {
  display: none; position: absolute; top: calc(100% + 3px); left: 0; right: 0;
  z-index: 300; background: #fff; border: 1px solid #ced4da;
  border-radius: .375rem; box-shadow: 0 4px 16px rgba(0,0,0,.1);
  max-height: 220px; overflow-y: auto; padding: .25rem 0;
}
.ms-wrap--open .ms-menu { display: block; }
.ms-item {
  display: flex; align-items: center; gap: .6rem;
  padding: .4rem .9rem; font-size: .875rem; cursor: pointer;
  color: #212529; margin: 0;
}
.ms-item:hover { background: #f0f4ff; }
.ms-item input { cursor: pointer; flex-shrink: 0; }
.ms-empty { padding: .5rem .9rem; font-size: .82rem; color: #6c757d; margin: 0; }
</style>
